import {
  Button,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Skeleton,
} from '@bpinternal/ui-kit-next'
import { useState } from 'react'
import { EmptyState } from '~/elements'
import { ConversationStatus } from '~/features/hitl/types'
import { useCurrentUserWorkspaceMember } from '~/hooks'
import { listConversationsInfiniteQuery } from '~/queries'
import { cn } from '~/utils'
import { ConversationItem } from './ConversationItem'
import { Boundary } from '~/components'
import { Conversation } from '@botpress/hitl-client'
import { UserCircle, UserRound, Users } from 'lucide-react'

type Props = {
  className?: string
  currentConversationId?: string
  allConversationsProps: Conversation[]
} & Parameters<typeof listConversationsInfiniteQuery>[0]

const ASSIGNMENT_FILTERS = {
  me: { label: 'My inbox', icon: <UserCircle size={20} /> },
  pending: { label: 'Unassigned', icon: <UserRound size={20} /> },
  all: { label: 'All', icon: <Users size={20} /> },
} as const

type AssignmentFilterValue = keyof typeof ASSIGNMENT_FILTERS

const STATUS_FILTER_OPTIONS = ['open', 'closed'] as const
type StatusFilterValue = (typeof STATUS_FILTER_OPTIONS)[number]

type ConversationBuckets = [Conversation[], Conversation[], Conversation[]]

const isConversationOpen = (conversation: Conversation) =>
  (['pending', 'assigned'] satisfies ConversationStatus[]).includes(conversation.status)

export const ConversationList = ({
  className,
  botId,
  workspaceId,
  currentConversationId,
  allConversationsProps,
}: Props) => {
  const currentWorkspaceMember = useCurrentUserWorkspaceMember({ workspaceId })

  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('open')

  const [myConversations, unassignedConversations, allConversations] = allConversationsProps
    .reduce<ConversationBuckets>(
      ([mine, unassigned, all], conv) => {
        if (conv.assignee?.workspaceMemberId === currentWorkspaceMember?.id) {
          mine.push(conv)
        } else if (!conv.assignee) {
          unassigned.push(conv)
        }

        all.push(conv)
        return [mine, unassigned, all]
      },
      [[], [], []] as ConversationBuckets
    )
    .map((conversations) =>
      conversations.filter((c) => (statusFilter === 'open' ? isConversationOpen(c) : !isConversationOpen(c)))
    )

  // Smart default assignee filter
  const [assigneeFilter, setAssigneeFilter] = useState<AssignmentFilterValue>(
    (myConversations?.length ?? 0) > 0 ? 'me' : 'pending'
  )

  const filteredConversations: Conversation[] =
    (assigneeFilter === 'me'
      ? myConversations
      : assigneeFilter === 'pending'
        ? unassignedConversations
        : allConversations) ?? []

  const getConversationBucketCount = (filter: AssignmentFilterValue): number => {
    switch (filter) {
      case 'me':
        return myConversations?.length ?? 0
      case 'pending':
        return unassignedConversations?.length ?? 0
      case 'all':
        return allConversations?.length ?? 0
      default:
        return 0
    }
  }

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <TooltipProvider>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Select value={assigneeFilter} onValueChange={(v: AssignmentFilterValue) => setAssigneeFilter(v)}>
            <SelectTrigger className="w-fit">
              <div className="flex items-center gap-2">
                {ASSIGNMENT_FILTERS[assigneeFilter].icon}
                <span>{ASSIGNMENT_FILTERS[assigneeFilter].label}</span>
                <span className="pr-1 text-xs">{getConversationBucketCount(assigneeFilter)}</span>
              </div>
            </SelectTrigger>
            <SelectContent>
              {Object.entries(ASSIGNMENT_FILTERS).map(([key, { label, icon }]) => (
                <SelectItem key={key} value={key} className="w-full">
                  <div className="flex w-full items-center gap-2">
                    {icon}
                    <span className="flex-1">{label}</span>
                    <span className="ml-auto text-xs">{getConversationBucketCount(key as AssignmentFilterValue)}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={(v: StatusFilterValue) => setStatusFilter(v)}>
            <Tooltip>
              <TooltipTrigger asChild>
                <SelectTrigger className="group w-fit border-none shadow-none focus-visible:ring-0">
                  <div className="flex items-center gap-2">
                    <span className="pr-1 capitalize group-hover:underline">{statusFilter}</span>
                  </div>
                </SelectTrigger>
              </TooltipTrigger>
              <TooltipContent>Filter by status</TooltipContent>
            </Tooltip>
            <SelectContent>
              {STATUS_FILTER_OPTIONS.map((v) => (
                <SelectItem key={v} value={v}>
                  <span className="capitalize">{v}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </TooltipProvider>
      <div className={cn(className, 'overflow-y-auto')}>
        <div className="m-auto flex h-full w-full flex-col gap-1">
          {filteredConversations.length === 0 ? (
            <EmptyState
              className="pt-6"
              title="No conversations"
              description={
                assigneeFilter === 'me'
                  ? 'Your inbox is empty, great job!'
                  : assigneeFilter === 'pending'
                    ? 'No unassigned conversations, your bot is doing a great job!'
                    : ''
              }
              primaryAction={
                assigneeFilter === 'me' ? (
                  <Button variant="outline" size="sm" onClick={() => setAssigneeFilter('pending')}>
                    Show Unassigned
                  </Button>
                ) : assigneeFilter === 'pending' ? (
                  <Button variant="outline" size="sm" onClick={() => setAssigneeFilter('all')}>
                    Show all
                  </Button>
                ) : null
              }
            />
          ) : (
            filteredConversations.map(
              (conversation, i) =>
                conversation.tags['upstream'] && (
                  <Boundary
                    key={conversation.id}
                    suspenseFallback={
                      <div className="space-y-2 p-3">
                        <div className="flex items-center gap-2">
                          <Skeleton className="h-6 w-6 rounded-full bg-gray-4" />
                          <Skeleton className="h-4 w-[250px] bg-gray-4" />
                        </div>
                        <Skeleton className="ml-8 h-4 w-[200px] bg-gray-4" />
                      </div>
                    }
                    onError={() => null}
                  >
                    <ConversationItem
                      key={conversation.id}
                      conversation={conversation}
                      botId={botId}
                      workspaceId={workspaceId}
                      index={i}
                      selected={currentConversationId === conversation.id}
                    />
                  </Boundary>
                )
            )
          )}
        </div>
      </div>
    </div>
  )
}
